import type React from 'react';

import { Button, Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate, TranslateHtml, useI18n } from '@change-corgi/core-react/i18n';

import { ErrorMessage } from '../../../ErrorMessage';
import { TermsDisclaimer } from '../../../TermsDisclaimer';

import { CameraSurface } from './components/CameraSurface';
import { RecordButton } from './components/RecordButton';
import { RecordingTips } from './components/RecordingTips';
import { ReviewActions } from './components/ReviewActions';
import { useVideoFirstConsolidatedAsk } from './hooks/useVideoFirstConsolidatedAsk';

/**
 * `v2_consolidated`'s ask screen: the whole capture flow on one screen.
 *
 * Where `v1_emotional` and `v3_impact` ask here and record on a screen of their own, this arm folds the
 * recorder and the preview into the ask, so `uploadStep` never leaves `ready` until the video is
 * submitted. That is the only difference between the arms; everything after submitting is shared.
 */
// eslint-disable-next-line max-lines-per-function
export const VideoFirstConsolidatedAsk: React.FC = () => {
	const {
		data: { phase, location, stream, secondsRemaining, takeLengthSec, blob, isUploading, errorMessage },
		actions: { handleRecordTap, handleTryAgain, handleSubmit, handleSkip },
	} = useVideoFirstConsolidatedAsk();
	const { translate } = useI18n();

	const isReviewing = phase === 'review';
	const isRecording = phase === 'recording';
	// True from the moment permission is granted, review included. Both the headline and the permission
	// note key off it, so the space they give up is reclaimed together.
	const hasCamera = phase !== 'prime';

	return (
		<div className="bg-background-base -mx-4 -mb-8 flex flex-col" data-testid="video-first-consolidated-ask">
			{/*
			 * Absolute against the page container, not this screen, so it reaches up behind the post-sign
			 * header. That is also why this screen renders outside the action card - the card is `relative`
			 * and `overflow-hidden`, so a gradient inside it could never clear the header.
			 */}
			<div className="bg-gradient-background-light-2 absolute inset-x-0 top-0 z-0 h-[311px]" aria-hidden>
				<div className="to-background-base absolute inset-x-0 bottom-0 h-[30%] bg-linear-to-b from-transparent" />
			</div>

			<div className="relative z-10 flex flex-col items-center gap-2 px-10 pt-4 pb-3 text-center">
				<Text
					as="h1"
					variant="heading"
					/*
					 * Steps down once the camera is live, giving its extra line back to the controls. A size
					 * token rather than a font-size class so it lands on the same scale as every other heading.
					 */
					size={hasCamera ? 'medium' : 'large'}
					fontWeight="bold"
					className="text-typography-strong text-pretty"
				>
					{location ? (
						<TranslateHtml
							value="corgi.petition_actions.video_first_ask.headline_emotional"
							replacements={{ location }}
						/>
					) : (
						<TranslateHtml value="corgi.petition_actions.video_first_ask.headline_emotional_no_location" />
					)}
				</Text>
				<Text as="p" variant="body" size="medium" className="text-typography-strong w-[310px] max-w-full text-pretty">
					<TranslateHtml value="corgi.petition_actions.video_first_ask.supporting_line_emotional" />
				</Text>
			</div>

			<div className="relative z-10 flex flex-col gap-4 px-8">
				{errorMessage && <ErrorMessage />}

				<div className="flex flex-col">
					<CameraSurface
						phase={phase}
						stream={stream}
						secondsRemaining={secondsRemaining}
						blob={blob}
						takeLengthSec={takeLengthSec}
					/>

					{isReviewing ? (
						<div className="mt-4">
							<ReviewActions isUploading={isUploading} onTryAgain={handleTryAgain} onSubmit={handleSubmit} />
						</div>
					) : (
						// Straddling the window's bottom edge, so the control reads as part of the camera rather
						// than as another button in the stack below it. The design's 13px, rounded to the nearest
						// spacing step.
						<div className="relative z-10 -mt-3.5 flex justify-center">
							<RecordButton isRecording={isRecording} disabled={isUploading} onClick={handleRecordTap} />
						</div>
					)}
				</div>

				{isRecording && (
					// Mirrors the recorder screen: announced once when the take starts, rather than a live region
					// reading the countdown out every second.
					<span
						aria-live="assertive"
						aria-atomic="true"
						className="absolute -m-px h-px w-px overflow-hidden border-0 p-0 [white-space:nowrap] [clip-path:inset(50%)] [clip:rect(0,0,0,0)]"
					>
						{translate('corgi.petition_actions.supporter_voice_video.record.record_button.label_stop')}
					</span>
				)}

				{!isReviewing && (
					/*
					 * Collapses its own row once the permission it describes has been granted, so the tips move up
					 * under the record button rather than sitting below a band of empty space.
					 */
					<div
						className={cn(
							'overflow-hidden transition-all duration-500 ease-out motion-reduce:transition-none',
							hasCamera ? 'max-h-0 opacity-0' : 'max-h-20 opacity-100',
						)}
						aria-hidden={hasCamera}
					>
						<Text
							as="p"
							variant="body"
							size="x-small"
							className="text-typography-weak mx-auto w-[266px] max-w-full text-center"
						>
							<Translate value="corgi.petition_actions.video_first_ask.permission_note" />
						</Text>
					</div>
				)}

				<RecordingTips isReviewing={isReviewing} />

				<TermsDisclaimer tone="preview" />
			</div>

			<div className="relative z-10 pt-4 pb-5 text-center">
				<Button
					variant="tertiary"
					size="small"
					onClick={handleSkip}
					disabled={isUploading || isRecording}
					data-testid="create-comment"
					data-qa="create-comment"
				>
					<Translate value="corgi.petition_actions.skip" />
				</Button>
			</div>
		</div>
	);
};
